/* eslint-disable no-template-curly-in-string */
import {
  TFormData,
  TFormField,
  IStringIndex,
  TJourneyConfig,
  TJourneyMode,
  TJourneyInfo,
  TLink,
  CreateNumberofClaimsText,
  OptionsFromTotalIncurred,
} from './types';
import { validateField } from './fieldValidation';
import { camelCase, get as lodash_get } from 'lodash';
import { getDataSourceOptions } from './codeLists/dataSource';
import { Engine } from 'json-rules-engine';
import { applyRule, handleUpdateRule } from './utils/rulesEngine';
import { formatToMoney } from './utils/common';
import { getEnvParams } from './utils';
import { differenceInYears } from 'date-fns';
import moment from 'moment';

type TOpt = {
  disabled: boolean;
  label: string;
  value: any;
  tooltip?: string;
  declineMessage?: string;
};

const setParentOptionDependents = (field: TFormField, conditions: IStringIndex<any>[], fields: TFormField[]) => {
  //console.log("setParentOptionDependents", field.name);
  for (let c of conditions) {
    if (c.anyOf) {
      setParentOptionDependents(field, c.anyOf, fields);
      continue;
    } else if (c.allOf) {
      setParentOptionDependents(field, c.allOf, fields);
      continue;
    } else if (c.notAnyOf) {
      setParentOptionDependents(field, c.notAnyOf, fields);
      continue;
    } else if (c.notAllOf) {
      setParentOptionDependents(field, c.notAllOf, fields);
      continue;
    } else if (c.anyGreaterThan) {
      setParentOptionDependents(field, c.anyGreaterThan, fields);
      continue;
    } else if (c.anySizeEquals) {
      setParentOptionDependents(field, c.anySizeEquals, fields);
      continue;
    } else if (c.anyContains) {
      setParentOptionDependents(field, c.anyContains, fields);
      continue;
    } else if (c.anyWithinRange) {
      setParentOptionDependents(field, c.anyWithinRange, fields);
      continue;
    } else if (c.startsWith) {
      setParentOptionDependents(field, c.startsWith, fields);
      continue;
    } else if (c.sumGreaterThan) {
      setParentOptionDependents(field, c.sumGreaterThan, fields);
      continue;
    }
    // Find parents from condition
    let conditionName = Object.keys(c)[0];
    if (conditionName.includes('.')) {
      conditionName = conditionName.split('.')[0];
    }
    let parents = fields.filter((field) => field.name === conditionName);
    for (let p of parents) {
      if (p.optionDependents === undefined) {
        p.optionDependents = [];
      }
      // Add field name to list of parent's dependents
      if (!p.optionDependents.includes(field.name)) {
        p.optionDependents.push(field.name);
      }
    }
  }
};
/**
 * Builds a map of all fields (including nested ones) by name for quick lookup.
 * Useful for finding parent fields when handling dependencies or conditions.
 * @returns {Map<string, TFormField[]>}
 */
function indexFields(root: TFormField[]): Map<string, TFormField[]> {
  const map = new Map<string, TFormField[]>();

  const walk = (list: TFormField[]) => {
    for (const f of list) {
      if (!map.has(f.name)) map.set(f.name, []);
      map.get(f.name)!.push(f);

      if (Array.isArray(f.fields)) walk(f.fields);
    }
  };

  walk(root);
  return map;
}

const setParentDependents = (field: TFormField, conditions: IStringIndex<any>[], fields: TFormField[]) => {
  field.hidden = true;
  const fieldIndex = indexFields(fields);

  for (let c of conditions) {
    // Recursively handle nested condition arrays
    if (c.anyOf) {
      setParentDependents(field, c.anyOf, fields);
      continue;
    } else if (c.allOf) {
      setParentDependents(field, c.allOf, fields);
      continue;
    } else if (c.notAnyOf) {
      setParentDependents(field, c.notAnyOf, fields);
      continue;
    } else if (c.notAllOf) {
      setParentDependents(field, c.notAllOf, fields);
      continue;
    } else if (c.anyGreaterThan) {
      setParentDependents(field, c.anyGreaterThan, fields);
      continue;
    } else if (c.anySizeEquals) {
      setParentDependents(field, c.anySizeEquals, fields);
      continue;
    } else if (c.anyContains) {
      setParentDependents(field, c.anyContains, fields);
      continue;
    } else if (c.anyWithinRange) {
      setParentDependents(field, c.anyWithinRange, fields);
      continue;
    } else if (c.startsWith) {
      setParentDependents(field, c.startsWith, fields);
      continue;
    } else if (c.sumGreaterThan) {
      setParentDependents(field, c.sumGreaterThan, fields);
      continue;
    }

    // Get the condition key name (field names or list)
    let conditionName = Object.keys(c)[0];

    // If conditionName has a dot, take the part before the dot
    if (conditionName.includes('.')) {
      conditionName = conditionName.split('.')[0];
    }

    // Handle multiple field names separated by commas
    const parentFieldNames = conditionName.split(',').map((name) => name.trim());

    for (const parentName of parentFieldNames) {
      // Get parents from index
      const parents = fieldIndex.get(parentName) ?? [];

      for (let p of parents) {
        if (p.dependents === undefined) {
          p.dependents = [];
        }
        // Add field.name to parent's dependents if not already present
        if (!p.dependents.includes(field.name)) {
          p.dependents.push(field.name);
        }
      }
    }
  }
};

export const getReferrals = (config: TJourneyConfig, values: IStringIndex<any>) => {
  const referrals: IStringIndex<any> = {};
  if (config.referrals) {
    for (let [fieldName, fieldValue] of Object.entries(config.referrals)) {
      if (values[fieldName] === fieldValue) {
        referrals[fieldName] = fieldValue;
      }
    }
  }
  return referrals;
};


export const getSectionedAnswers = (config: TJourneyConfig, values: IStringIndex<any>) => {
  const answers: IStringIndex<any> = {};
  const finalIndex = config?.sections?.findIndex((section) => section.isFinal) ?? config?.sections?.length - 1;

  const processField = (field: any): any | null => {
    if (field.restrictToMode === 'MTA') return null;

    if (Array.isArray(field.fields)) {
      const childAnswers = field.fields.map(processField).filter(Boolean); // remove nulls

      return {
        name: camelCase(field.name),
        question: field.label || field.title,
        type: field.type,
        fields: childAnswers,
      };
    }

    if (values[field.name] !== undefined) {
      return {
        name: camelCase(field.name),
        question: field.label || field.title,
        type: field.type,
        answer: values[field.name],
      };
    }

    return null;
  };

  for (let idx = 0; idx < finalIndex + 1; idx++) {
    const section = config.sections[idx];
    const sectionName = camelCase(section.name);

    answers[sectionName] = section.fields.map(processField).filter(Boolean); // keep only valid fields
  }

  return answers;
};


export const initDependencies = (fields: TFormField[], mode: TJourneyMode) => {
  for (let f of fields) {
    if (f.restrictToMode !== undefined && !f.restrictToMode.includes(mode)) {
      console.log(`${f.name} is restricted to ${f.restrictToMode} (current mode is ${mode})`);
      f.hidden = true;
    } else {
      if (f.dependsOn?.allOf?.length) {
        setParentDependents(f, f.dependsOn.allOf, fields);
      } else if (f.dependsOn?.anyOf?.length) {
        setParentDependents(f, f.dependsOn.anyOf, fields);
      } else if (f.dependsOn?.notAllOf?.length) {
        setParentDependents(f, f.dependsOn.notAllOf, fields);
      } else if (f.dependsOn?.notAnyOf?.length) {
        setParentDependents(f, f.dependsOn.notAnyOf, fields);
      } else if (f.dependsOn?.anyGreaterThan?.length) {
        setParentDependents(f, f.dependsOn.anyGreaterThan, fields);
      } else if (f.dependsOn?.anySizeEquals?.length) {
        setParentDependents(f, f.dependsOn.anySizeEquals, fields);
      } else if (f.dependsOn?.anyContains?.length) {
        setParentDependents(f, f.dependsOn.anyContains, fields);
      } else if (f.dependsOn?.anyWithinRange?.length) {
        setParentDependents(f, f.dependsOn.anyWithinRange, fields);
      } else if (f.dependsOn?.startsWith?.length) {
        setParentDependents(f, f.dependsOn.startsWith, fields);
      } else if (f.dependsOn?.sumGreaterThan?.length) {
        setParentDependents(f, f.dependsOn.sumGreaterThan, fields);
      }

      if (f.optionDependencies?.length) {
        for (let d of f.optionDependencies) {
          if (d === null) {
            continue;
          }
          if (d.allOf?.length) {
            setParentOptionDependents(f, d.allOf, fields);
          } else if (d.anyOf?.length) {
            setParentOptionDependents(f, d.anyOf, fields);
          } else if (d.notAllOf?.length) {
            setParentOptionDependents(f, d.notAllOf, fields);
          } else if (d.notAnyOf?.length) {
            setParentOptionDependents(f, d.notAnyOf, fields);
          } else if (d.anyGreaterThan?.length) {
            setParentOptionDependents(f, d.anyGreaterThan, fields);
          } else if (d.anySizeEquals?.length) {
            setParentOptionDependents(f, d.anySizeEquals, fields);
          } else if (d.anyContains?.length) {
            setParentOptionDependents(f, d.anyContains, fields);
          } else if (d.anyWithinRange?.length) {
            setParentOptionDependents(f, d.anyWithinRange, fields);
          } else if (d.startsWith?.length) {
            setParentOptionDependents(f, d.startsWith, fields);
          } else if (d.sumGreaterThan?.length) {
            setParentOptionDependents(f, d.sumGreaterThan, fields);
          }
        }
      }

      if (f.fields?.length) {
        initDependencies(f.fields ?? [], mode);
      }
    }
  }
};

export const getFormData = (
  fields: TFormField[],
  initialFormData: TFormData,
  mode: TJourneyMode,
  config: TJourneyConfig,
): TFormData => {
  const cleanValues: IStringIndex<any> = {};
  for (let [fieldName, fieldValue] of Object.entries(initialFormData.values)) {
    if (initialFormData.validations[fieldName] === false) {
      // console.log("Not populating", fieldName);
      continue;
    }
    if (typeof fieldValue === 'string') {
      cleanValues[fieldName] = fieldValue.replace(/\s+/g, ' ').trim();
    } else {
      cleanValues[fieldName] = fieldValue;
    }
  }
  const initialValidations = initialFormData.validations;
  const data: TFormData = {
    values: { ...cleanValues },
    validations: { ...initialValidations },
  };
  // console.log("==== FIELDS BEFORE ====");
  // console.log(JSON.stringify(fields, null, 2));
  for (let f of fields) {
    const initialValue = cleanValues[f.name];
    data.values[f.name] = initialValue;
    const validation = validateField(f, initialValue, cleanValues);
    data.validations[f.name] = validation;
  }
  for (let f of fields) {
    // f.dependents && console.log(`Field "${f.name}" has dependents ${JSON.stringify(f.dependents)}`);
    // f.optionDependents &&
    //   console.log(`Field "${f.name}" has option dependents ${JSON.stringify(f.optionDependents)}`);
    checkDependents(f, fields, data.values, data.validations, config, mode);
  }

  // console.log("==== FIELDS AFTER ====");
  // console.log(JSON.stringify(fields, null, 2));
  return data;
};

export const getFieldOptions = (field: TFormField, formData: TFormData): TOpt[] => {
  if (field.options?.includes('###EXTERNAL' as any)) {
    return [
      {
        disabled: false,
        label: 'EXTERNAL',
        value: 'EXTERNAL',
      },
    ];
  }
  const options = field.dataSource ? getDataSourceOptions(field.dataSource) : (field.options ?? []);
  return (
    options.map((option, index) => {
      const dependency = field.optionDependencies ? field.optionDependencies[index] : null;
      const disabled = dependency ? !conditionMet(dependency, formData.values, formData.validations) : false;
      return typeof option === 'string'
        ? {
            label: replacePlaceholders(formData.values, option),
            value: replacePlaceholders(formData.values, option),
            disabled,
          }
        : { ...option, disabled };
    }) || []
  );
};

export const createNumberofClaimsText: CreateNumberofClaimsText = (period, options) => {
  const coverPeriod = parseInt(period);
  const numberOfClaims = String(coverPeriod / 4);
  const policyPriodForCoverText = period === '12' ? 'a year' : period + ' months';
  let claimText = `Claim up to ${numberOfClaims} times in ${policyPriodForCoverText}`;

  const newOptions = [...options];
  for (let element of newOptions) {
    if (element?.label === 'coverClaimText') {
      element.label = claimText;
    }
  }

  return newOptions;
};

export const calculateTotalIncurredValue: OptionsFromTotalIncurred = (formData, options) => {
  const currentReserve1 = formData?.currentReserve1;
  const amountPaidSoFar1 = formData?.AmountPaidSoFar1;

  const currentReserve2 = formData?.currentReserve2;
  const amountPaidSoFar2 = formData?.AmountPaidSoFar2;

  if (currentReserve1 === undefined || amountPaidSoFar1 === undefined) {
    return options;
  }

  const totalIncurred1 = currentReserve1 + amountPaidSoFar1;
  formData.totalIncurred1 = totalIncurred1;

  const newOptions = [...options];
  for (let element of newOptions) {
    if (element?.label === 'Total Incurred 1') {
      element.value = `£${totalIncurred1}`;
    }
  }

  if (currentReserve2 === undefined || amountPaidSoFar2 === undefined) {
    return newOptions;
  }

  const totalIncurred2 = currentReserve2 + amountPaidSoFar2;
  formData.totalIncurred2 = totalIncurred2;

  const newOptions2 = [...newOptions];
  for (let element of newOptions2) {
    if (element?.label === 'Total Incurred 2') {
      element.value = `£${totalIncurred2}`;
    }
  }
  return newOptions2;
};

export const basisForCoverLogic = (yearOfManufacture: any, formData: any, options: any) => {
  const currentDate = moment();

  const caravanAge = currentDate.diff(yearOfManufacture, 'years');
  if (caravanAge > 10 && options.name === 'yearOfManufacture') {
    formData.values.whatBasisOfCoverIsRequired = 'Market Value';
  }
};

export const getLinks = (info: TJourneyInfo, formData: TFormData): TLink[] => {
  const insurerName = formData?.values?.quote?.insurerName;

  if (insurerName === 'Helvetia Schweizerische Versicherungsgesellschaft in Liechtenstein AG' && info.HelvetiaLinks) {
    info.links = info.HelvetiaLinks;
  }

  if (
    (insurerName?.includes('Collinson') || formData?.values?.insurerName?.includes('Collinson')) &&
    info.CollinsonLinks
  ) {
    info.links = info.CollinsonLinks;
  }

  return info.links
    ? info.links.filter((link) => {
        if (link.dependsOn === undefined) {
          return true;
        }
        return conditionMet(link.dependsOn, formData.values, formData.validations);
      })
    : [];
};

export const checkOptionDependents = (
  field: TFormField,
  fields: TFormField[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  // Check if option dependencies are met
  for (let name of field.optionDependents || []) {
    const dependentFields = fields.filter((f) => f.name === name);
    for (let dependentField of dependentFields) {
      const options = getFieldOptions(dependentField, {
        values: newValues,
        validations: newValidations,
      });
      // Try to determine selected option
      const selected = options?.find((opt) => {
        if (typeof opt === 'string') {
          return opt === newValues[dependentField.name];
        } else {
          return opt.value === newValues[dependentField.name];
        }
      });
      if (selected?.disabled) {
        newValues[dependentField.name] = undefined;
        newValidations[dependentField.name] = validateField(dependentField, undefined, newValues);
        //
        if (dependentField.optionDependents) {
          checkOptionDependents(dependentField, fields, newValues, newValidations);
        }
      }
    }
  }
};

const { mode: journeyMode } = getEnvParams();
export const checkDependents = (
  field: TFormField,
  fields: TFormField[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
  config?: TJourneyConfig,
  mode?: TJourneyMode,
) => {
  mode = mode ?? journeyMode ?? 'DEFAULT';
  if (field.dependents?.length) {
    for (let name of field.dependents) {
      const dependentFields = fields.filter((f) => f.name === name);
      for (let dependentField of dependentFields) {
        handleDependsOn(dependentField, newValues, newValidations, config);
        checkDependents(dependentField, fields, newValues, newValidations, config, mode);
      }
    }
  } else if (field.dependsOn) {
    if (field.restrictToMode !== undefined && !field.restrictToMode.includes(mode as any)) {
      console.log(`${field.name} is restricted to ${field.restrictToMode} (current mode is ${mode})`);
      field.hidden = true;
    } else {
      handleDependsOn(field, newValues, newValidations, config);
    }
  } else if (field?.fields) {
    for (let subField of field.fields) {
      checkDependents(subField, fields, newValues, newValidations, config, mode);
    }
  }
};

export const conditionMet = (
  condition: IStringIndex<any>,
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
  conditionName?: string,
): boolean => {
  const conditionTypes = {
    anyOf: anyConditionsMet,
    allOf: allConditionsMet,
    notAnyOf: notAnyConditionsMet,
    notAllOf: notAllConditionsMet,
    anyGreaterThan: anyGreaterThanMet,
    anySizeEquals: anySizeEqualsMet,
    anyContains: anyContainsMet,
    anyWithinRange: anyWithinRangeMet,
    startsWith: startsWithMet,
    sumGreaterThan: sumGreaterThanMet,
  };

  for (const key in conditionTypes) {
    if (condition[key]) {
      // e.g if anyOf => anyConditionsMet(condition.anyOf, newValues, newValidations);
      return conditionTypes[key as keyof typeof conditionTypes](condition[key], newValues, newValidations);
    }
  }

  const parentName = Object.keys(condition)[0];
  // mainly for sumGreaterThan
  let actualValues;
  if (parentName?.includes(',')) {
    actualValues = parentName.split(',').map((p: any) => lodash_get(newValues, p));
  }
  const actualValue = lodash_get(newValues, parentName);
  const expectedValue = condition[parentName];

  const isArrayField = parentName?.includes('.index.');
  const handleArrayField = () => {
    const paths = parentName?.split('.');
    const listValues = lodash_get(newValues, paths[0], []);
    const itemFieldName = paths?.[paths.length - 1];
    return { listValues, itemFieldName };
  };

  const evaluateRange = (min: number, max: number, value: any): boolean => {
    const numberValue = Number(value);
    return numberValue >= min && numberValue <= max;
  };

  const evaluateArrayField = (comparator: (v: any) => boolean): boolean => {
    if (!isArrayField) return false;
    const { listValues, itemFieldName } = handleArrayField();
    return listValues?.some((v: any) => comparator(v[itemFieldName]));
  };

  // Named condition handlers
  if (conditionName === 'anyWithinRange' && Array.isArray(expectedValue?.valueRange)) {
    const [min, max] = expectedValue.valueRange;
    return isArrayField ? evaluateArrayField((v) => v >= min && v <= max) : evaluateRange(min, max, actualValue);
  }

  if (conditionName === 'startsWith' && typeof expectedValue === 'string') {
    return isArrayField
      ? evaluateArrayField((v) => v.startsWith(expectedValue))
      : actualValue?.startsWith(expectedValue);
  }

  if (conditionName === 'sumGreaterThan' && typeof expectedValue === 'number') {
    // if one of the fields is date then get age
    const updatedValues = actualValues?.map((v: any) => {
      if (typeof v === 'string' && !isNaN(new Date(v).getTime())) {
        return differenceInYears(new Date(), new Date(v));
      }
      return v;
    });
    const sum = Math.abs(updatedValues?.reduce((a: number, b: number) => a + b, 0));
    return sum > expectedValue;
  }

  // when expectedValue is an object mostly for older products
  if (typeof expectedValue === 'object' && expectedValue !== null) {
    if (expectedValue.includesValue !== undefined && Array.isArray(actualValue)) {
      return actualValue.some((v) => v.value === expectedValue.includesValue);
    }

    if (Array.isArray(expectedValue.valueRange) && expectedValue.valueRange.length === 2) {
      return evaluateRange(expectedValue.valueRange[0], expectedValue.valueRange[1], actualValue);
    }

    if (Array.isArray(expectedValue.ageRange) && expectedValue.ageRange.length === 2 && actualValue) {
      const now = moment();
      const format = expectedValue.dateFormat ?? (actualValue.length === 4 ? 'YYYY' : 'DD-MM-YYYY');
      const date = moment(actualValue, format);
      if (date.isValid()) {
        const age = now.diff(date, expectedValue.unit ?? 'years');
        return age >= expectedValue.ageRange[0] && age <= expectedValue.ageRange[1];
      }
    }

    return false;
  }

  if (expectedValue === 'any') {
    return newValidations[parentName] === true && actualValue !== undefined;
  }

  if (conditionName === 'anyGreaterThan') {
    if (typeof expectedValue === 'number') {
      return isArrayField
        ? evaluateArrayField((v) => v > expectedValue)
        : Array.isArray(actualValue)
          ? actualValue.length > expectedValue
          : actualValue > expectedValue;
    }
  }

  if (conditionName === 'anySizeEquals') {
    return isArrayField
      ? evaluateArrayField((v) => v?.length === expectedValue)
      : Array.isArray(actualValue) && actualValue.length === expectedValue;
  }

  if (conditionName === 'anyContains') {
    return isArrayField
      ? evaluateArrayField((v) => v?.includes?.(expectedValue))
      : Array.isArray(actualValue) && actualValue.some((v) => v.value === expectedValue);
  }

  // Default comparison
  return isArrayField ? evaluateArrayField((v) => v === expectedValue) : expectedValue === actualValue;
};

const anyWithinRangeMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allMet = conditionMet(c, newValues, newValidations, 'anyWithinRange');
    if (allMet) {
      return true;
    }
  }
  return false;
};

const allConditionsMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allMet = conditionMet(c, newValues, newValidations);
    if (allMet === false) {
      return false;
    }
  }
  return true;
};

const anyGreaterThanMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allGreater = conditionMet(c, newValues, newValidations, 'anyGreaterThan');
    if (allGreater) {
      return true;
    }
  }
  return false;
};
const anySizeEqualsMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allGreater = conditionMet(c, newValues, newValidations, 'anySizeEquals');
    if (allGreater) {
      return true;
    }
  }
  return false;
};

const anyContainsMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allGreater = conditionMet(c, newValues, newValidations, 'anyContains');
    if (allGreater) {
      return true;
    }
  }
  return false;
};

const anyConditionsMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const anyMet = conditionMet(c, newValues, newValidations);
    if (anyMet) {
      return true;
    }
  }
  return false;
};

const notAllConditionsMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  return !allConditionsMet(conditions, newValues, newValidations);
};

const notAnyConditionsMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  return !anyConditionsMet(conditions, newValues, newValidations);
};

const sumGreaterThanMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allGreater = conditionMet(c, newValues, newValidations, 'sumGreaterThan');
    if (allGreater) {
      return true;
    }
  }
  return false;
};

const startsWithMet = (
  conditions: IStringIndex<any>[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
) => {
  for (let c of conditions) {
    const allGreater = conditionMet(c, newValues, newValidations, 'startsWith');
    if (allGreater) {
      return true;
    }
  }
  return false;
};

function handleDependsOn(
  dependentField: TFormField,
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
  config: TJourneyConfig | undefined,
) {
  console.log(`Checking dependencies for field "${dependentField.name}" with value `, dependentField.dependsOn);
  let conditionsMet = false;
  if (dependentField.dependsOn?.allOf) {
    conditionsMet = allConditionsMet(dependentField.dependsOn.allOf, newValues, newValidations);
  } else if (dependentField.dependsOn?.anyOf) {
    conditionsMet = anyConditionsMet(dependentField.dependsOn.anyOf, newValues, newValidations);
  } else if (dependentField.dependsOn?.notAllOf) {
    conditionsMet = notAllConditionsMet(dependentField.dependsOn.notAllOf, newValues, newValidations);
  } else if (dependentField.dependsOn?.notAnyOf) {
    conditionsMet = notAnyConditionsMet(dependentField.dependsOn.notAnyOf, newValues, newValidations);
  } else if (dependentField.dependsOn?.anyGreaterThan) {
    conditionsMet = anyGreaterThanMet(dependentField.dependsOn.anyGreaterThan, newValues, newValidations);
  } else if (dependentField.dependsOn?.anySizeEquals) {
    conditionsMet = anySizeEqualsMet(dependentField.dependsOn.anySizeEquals, newValues, newValidations);
  } else if (dependentField.dependsOn?.anyContains) {
    conditionsMet = anyContainsMet(dependentField.dependsOn.anyContains, newValues, newValidations);
  } else if (dependentField.dependsOn?.anyWithinRange) {
    conditionsMet = anyWithinRangeMet(dependentField.dependsOn.anyWithinRange, newValues, newValidations);
  } else if (dependentField.dependsOn?.startsWith) {
    conditionsMet = startsWithMet(dependentField.dependsOn.startsWith, newValues, newValidations);
  } else if (dependentField.dependsOn?.sumGreaterThan) {
    conditionsMet = sumGreaterThanMet(dependentField.dependsOn.sumGreaterThan, newValues, newValidations);
  }

  if (dependentField.name === 'pcMtaQuoteSummary' && newValues['paymentFrequency'] === 'MONTHLY') {
    if (Number(newValues?.MTA?.mtaFee) === 0 && Number(newValues?.MTA?.mtaAdjustment) === 0) {
      dependentField.hidden = true;
      conditionsMet = false;
    }
  }

  if (dependentField.hidden !== !conditionsMet) {
    dependentField.hidden = !conditionsMet;
    console.log(`Field "${dependentField.name}" is now ${dependentField.hidden ? 'hidden' : 'visible'}`);
    if (dependentField.hidden) {
      if (dependentField.type === 'setter') {
        for (let k of dependentField.setterParams!.keys) {
          newValues[k] = undefined;
        }
      } else {
        newValues[dependentField.name] = config?.initialValues?.[dependentField.name] ?? undefined;
        newValues[dependentField.name] = config?.initialValues?.[dependentField.name] ?? undefined;
      }
    }
    // auto opens array modal when field becomes visible
    if (!dependentField.hidden && dependentField.type === 'array' && dependentField?.arrayParams?.autoOpenModal) {
      dependentField.arrayParams.autoOpen = true;
    }
    newValidations[dependentField.name] = validateField(dependentField, newValues[dependentField.name], newValues);
  }
}

export async function checkRules(
  field: TFormField,
  allFields: TFormField[],
  newValues: IStringIndex<any>,
  newValidations: IStringIndex<boolean>,
  config: TJourneyConfig,
) {
  if (field.dependents && !field.rules) {
    const dependentFields = field.dependents.map((name) => allFields.find((f) => f.name === name));
    for (let f of dependentFields) {
      if (f?.rules) {
        const result = await applyRule(f.rules, newValues);
        if (result && result.conditionMet) {
          if (result.results?.event?.type === 'show') {
            const messages = result.results?.event?.params?.messages;
            if (messages) {
              const conditions = result.results.conditions as any;
              const op = conditions.operator;
              const metCondition = conditions[op].find((condition: any) => condition.result === true);
              const message = replacePlaceholders(newValues, messages[metCondition.operator]);
              f.title = message;
            }
            const initialValue = config?.initialValues![f.name];
            if (!newValues[f.name]) {
              newValues[f.name] = initialValue;
            }
            f.hidden = false;
            newValidations[f.name] = validateField(f, newValues[f.name], newValues);
          } else if (result && result.results?.event?.type === 'updateOptions') {
            const options = result.results?.event?.params?.options;
            if (options) {
              f.options = options;
            }
            f.hidden = false;
          }
        } else if (f?.rules?.event?.type === 'updateOptions') {
          const defaultOptions = f.rules.event?.params?.defaultOptions;
          if (defaultOptions) f.options = defaultOptions;
          f.hidden = false;
        } else {
          f.hidden = true;
          newValues[f.name] = undefined;
          newValidations[f.name] = validateField(f, newValues[f.name], newValues);
        }
      }
    }
  } else if (field.rules) {
    const result = await applyRule(field.rules, newValues);
    const ruleType = field.rules?.event?.type;
    switch (ruleType) {
      case 'update':
      case 'updateMultiple':
        handleUpdateRule(result, newValues, newValidations, field);
        break;
      default:
        break;
    }
  }
}
/**
 * takes a string and if it contains references to object keys e.g ${firstName} it replaces it with its value
 * @param obj form data
 * @param str string to replace
 * @param stringify if the value is an object it returns the values as a string
 * @returns modified string
 */
export function replacePlaceholders(obj: any, str: string, stringify = true, idx?: number) {
  if (!str) return '';
  const regex = /\${(.*?)}/g;

  const replacedStr = str?.replace(regex, (match: any, key: any) => {
    const keys = key.split('||').map((k: any) => k.trim());

    for (let key of keys) {
      const value = key === 'index' ? idx : getValueFromNestedKey(obj, key);
      if (value !== undefined) {
        if (typeof value === 'object' && stringify) {
          return Object.values(value);
        }
        if (typeof value === 'number' && str.includes('£')) {
          return formatToMoney(value);
        }
        return value;
      } else {
        return 'N/A';
      }
    }

    return '';
  });

  return replacedStr.replace(/,,/g, ', ');
}

function getValueFromNestedKey(obj: any, key: any) {
  const keys = key.split('.');
  let value = obj;

  for (let nestedKey of keys) {
    if (!value || !value.hasOwnProperty(nestedKey)) {
      return undefined;
    }

    value = value[nestedKey];
  }

  return value;
}
